import { createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'
import { DataStatus } from '@repo/ui/utils'

import { AsyncThunkConfig, RejectValue, sudoStoreActions } from '@/store/actions'
import { SudoContextCaseWorker, SudoContextUser } from '@/store/sudo'
import { Organization } from '@/store/user'

export const user = {
  //
  // APP
  //
  app: {
    updatePassword: createAsyncThunk<boolean, { password: string }, AsyncThunkConfig>(
      'UPDATE_USER_PASSWORD',
      async (fields, { extra, fulfillWithValue, rejectWithValue }) => {
        const { api } = extra
        const ok = await api.me.patchPassword(fields)
        if (hasError(ok)) return rejectWithValue(handleError(ok))
        return fulfillWithValue(ok)
      },
    ),

    signUp: createAsyncThunk<
      boolean,
      { email: string; firstName: string; lastName: string },
      AsyncThunkConfig
    >('SIGNUP', async (fields, { extra, fulfillWithValue, rejectWithValue }) => {
      const { altApi } = extra
      const res = await altApi.auth.signup(fields)
      if (hasError(res)) return rejectWithValue(handleError(res))
      return fulfillWithValue(res.success)
    }),

    requestPasswordReset: createAsyncThunk<boolean, { email: string }, AsyncThunkConfig>(
      'REQUEST_PASSWORD_RESET',
      async (fields, { extra, fulfillWithValue, rejectWithValue }) => {
        const { api } = extra
        const ok = await api.auth.requestPasswordReset(fields)
        if (hasError(ok)) return rejectWithValue(handleError(ok))
        return fulfillWithValue(ok)
      },
    ),

    resetPassword: createAsyncThunk<
      boolean,
      { token: string; password: string; email: string },
      AsyncThunkConfig
    >('RESET_PASSWORD', async (fields, { extra, fulfillWithValue, rejectWithValue }) => {
      const { api } = extra
      const ok = await api.auth.resetPassword(fields)
      if (hasError(ok)) return rejectWithValue(handleError(ok))
      return fulfillWithValue(ok)
    }),
  },

  //
  // SUDO
  //
  sudo: {
    create: createAsyncThunk<
      { users: SudoContextUser[] },
      {
        fields: Omit<SudoContextUser, 'id'> & { password: string }
      },
      AsyncThunkConfig
    >('SUDO_CREATE_USER', async (payload, { extra, fulfillWithValue, rejectWithValue }) => {
      const { sudoApi } = extra
      const users = await sudoApi.users.createOne(payload)
      if (hasError(users)) return rejectWithValue(handleError(users))
      return fulfillWithValue({ users })
    }),

    update: createAsyncThunk<
      { user: SudoContextUser },
      {
        fields: Partial<
          Pick<
            SudoContextUser,
            'firstName' | 'lastName' | 'email' | 'superAdmin' | 'organizationIds'
          >
        >
        userId: string
      },
      AsyncThunkConfig
    >('SUDO_UPDATE_USER', async (payload, { extra, fulfillWithValue, rejectWithValue }) => {
      const { sudoApi } = extra
      const user = await sudoApi.users.patchOne(payload)
      if (hasError(user)) return rejectWithValue(handleError(user))
      return fulfillWithValue({ user })
    }),

    updateOrganizationsAccess: createAsyncThunk<
      { organizations: Organization[] },
      {
        organizationIds: SudoContextUser['organizationIds']
        userId: string
      },
      AsyncThunkConfig
    >(
      'SUDO_UPDATE_USER_ORGANIZATIONS_ACCESS',
      async (
        { organizationIds, userId },
        { extra, dispatch, fulfillWithValue, rejectWithValue },
      ) => {
        const { api } = extra
        await dispatch(
          sudoStoreActions.user.update({
            fields: {
              organizationIds,
            },
            userId,
          }),
        )
        await dispatch(sudoStoreActions.user.select({ userId }))

        const data = await api.me.getInitialValues()
        if (hasError(data)) return rejectWithValue(handleError(data))
        return fulfillWithValue({ organizations: data.organizations })
      },
    ),

    updatePassword: createAsyncThunk<
      void,
      {
        password: string
        userId: string
      },
      AsyncThunkConfig
    >('SUDO_UPDATE_PASSWORD', async (payload, { extra, rejectWithValue }) => {
      const { sudoApi } = extra
      const ok = await sudoApi.users.patchOnePassword(payload)
      if (ok) return
      return rejectWithValue(RejectValue.NoUpdate)
    }),

    enable: createAsyncThunk<
      { user: SudoContextUser },
      {
        userId: string
      },
      AsyncThunkConfig
    >('SUDO_ENABLE_USER', async (payload, { extra, fulfillWithValue, rejectWithValue }) => {
      const { sudoApi } = extra
      const user = await sudoApi.users.enableOne(payload)
      if (hasError(user)) return rejectWithValue(handleError(user))
      return fulfillWithValue({ user })
    }),

    disable: createAsyncThunk<
      { user: SudoContextUser },
      {
        userId: string
      },
      AsyncThunkConfig
    >('SUDO_DISABLE_USER', async (payload, { extra, fulfillWithValue, rejectWithValue }) => {
      const { sudoApi } = extra
      const user = await sudoApi.users.disableOne(payload)
      if (hasError(user)) return rejectWithValue(handleError(user))
      return fulfillWithValue({ user })
    }),

    select: createAsyncThunk<
      { caseWorkers: SudoContextCaseWorker[] },
      { userId: string },
      AsyncThunkConfig
    >(
      'SUDO_SELECT_USER',
      async (payload, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { sudoApi } = extra
        const organizations = getState().sudo.organizations
        if (!DataStatus.isFulfilled(organizations))
          return rejectWithValue(RejectValue.NoOrganization)
        const caseWorkers = await sudoApi.users.getCaseWorkersFromOne(payload)
        if (hasError(caseWorkers)) return rejectWithValue(handleError(caseWorkers))
        return fulfillWithValue({ caseWorkers })
      },
    ),
  },
}
